// src/pages/Usage.tsx
import React from 'react';
import { Card, Progress, Statistic, Row, Col, Alert, Button } from 'antd';
import { useGetIdentity } from '@refinedev/core';
import {
  VideoCameraOutlined,
  FileTextOutlined,
  CalendarOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  getReviewLimit,
  getOverageRate,
  getOverageCount,
  getPlanDisplayName,
  hasActivePlan,
} from '../utils/planLimits';

const Usage: React.FC = () => {
  const { data: user } = useGetIdentity<any>();

  const plan: string = user?.plan;
  const isActive = hasActivePlan(plan);
  const used = user?.usage?.videosThisMonth || 0;
  const included = getReviewLimit(plan);
  const overageCount = getOverageCount(plan, used);
  const overageRate = getOverageRate(plan);
  const overageCost = overageCount * overageRate;
  const includedPercent = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* No active plan */}
      {!isActive && (
        <Alert
          message="No active plan"
          description="Choose a plan to start creating reviews. Your first month's base is free."
          type="info"
          showIcon
          className="mb-6"
          action={
            <Link to="/billing">
              <Button type="primary" size="small">Choose a Plan</Button>
            </Link>
          }
        />
      )}

      {/* Overage notice */}
      {isActive && overageCount > 0 && (
        <Alert
          message="You're past your included reports"
          description={`${overageCount} additional report${overageCount === 1 ? '' : 's'} this cycle at $${overageRate} each — an estimated $${overageCost} in overage, billed on your next invoice.`}
          type="warning"
          showIcon
          className="mb-6"
        />
      )}

      {/* Current Month Usage */}
      <Card title="Current Month Usage" className="mb-6">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Card className="text-center">
              <div className="flex items-center justify-center mb-4">
                <VideoCameraOutlined className="text-3xl text-blue-500" />
              </div>
              <Statistic
                title="Reports This Month"
                value={used}
                suffix={included > 0 ? `/ ${included} included` : ''}
              />
              {included > 0 && (
                <Progress
                  percent={includedPercent}
                  status={overageCount > 0 ? 'exception' : 'normal'}
                  className="mt-4"
                />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="text-center">
              <div className="flex items-center justify-center mb-4">
                <FileTextOutlined className="text-3xl text-orange-500" />
              </div>
              <Statistic
                title="Overage This Cycle"
                value={overageCount}
                suffix={overageCount > 0 ? `× $${overageRate} = $${overageCost}` : ''}
              />
              <div className="text-gray-500 mt-2 text-sm">
                {isActive
                  ? `Reports beyond ${included} bill at $${overageRate} each`
                  : 'No active plan'}
              </div>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* All Time Statistics */}
      <Row gutter={16} className="mb-6">
        <Col xs={24} md={12}>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <TrophyOutlined />
                  <span>Total Videos</span>
                </div>
                <div className="text-3xl font-bold">{user?.usage?.videosAllTime || 0}</div>
              </div>
              <VideoCameraOutlined className="text-4xl text-blue-500 opacity-20" />
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <TrophyOutlined />
                  <span>Total Scripts</span>
                </div>
                <div className="text-3xl font-bold">{user?.usage?.scriptsAllTime || 0}</div>
              </div>
              <FileTextOutlined className="text-4xl text-green-500 opacity-20" />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Plan Information */}
      <Card title="Current Plan Details">
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <div className="text-center p-4 border rounded">
              <div className="text-lg font-semibold mb-2">Plan</div>
              <div className="text-2xl font-bold text-blue-600">
                {getPlanDisplayName(plan)}
              </div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="text-center p-4 border rounded">
              <div className="text-lg font-semibold mb-2">Included / Month</div>
              <div className="text-2xl font-bold">{isActive ? included : '—'}</div>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div className="text-center p-4 border rounded">
              <div className="text-lg font-semibold mb-2">Overage Rate</div>
              <div className="text-2xl font-bold">{isActive ? `$${overageRate}` : '—'}</div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Usage History */}
      <Card title="Usage Timeline" className="mt-6">
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 border rounded">
            <CalendarOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="font-medium">This Month</div>
              <div className="text-sm text-gray-600">
                {used} report{used === 1 ? '' : 's'} generated
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">
                {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4 p-4 border rounded bg-gray-50">
            <CalendarOutlined className="text-gray-400" />
            <div className="flex-1">
              <div className="font-medium">All Time</div>
              <div className="text-sm text-gray-600">
                Total: {user?.usage?.videosAllTime || 0} videos, {user?.usage?.scriptsAllTime || 0} scripts
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">
                Since {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Usage;
